import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { QUICK_ACTION_CATALOG, DEFAULT_QUICK_ACTION_KEYS } from '@neuroedge/shared';
import { NavIcon, type NavIconName } from '../ui';
import { useAuth } from '../../auth/AuthContext';
import { AddFollowUpQuickModal } from '../../modules/dashboard/AddFollowUpQuickModal';
import { CustomizeQuickActionsModal } from '../../modules/dashboard/CustomizeQuickActionsModal';
import './quick-actions.css';

/** One glyph per catalogue action, from the sidebar's icon set. */
const ICON: Record<string, NavIconName> = {
  new_quotation: 'fileText',
  add_task: 'checkSquare',
  add_followup: 'bell',
  new_contact: 'user',
  import_contacts: 'users',
  new_lead: 'target',
  new_pipeline: 'columns',
  new_product: 'package',
  import_products: 'layers',
  scan_catalog: 'clipboard',
  new_automation: 'zap',
  new_campaign: 'megaphone',
};

/**
 * Topbar "Quick actions" menu - the user's shortcut set (New Quotation, Add
 * Task, Add Follow-up, ...) reachable from every screen. The set is a
 * per-user preference (`user.dashboardQuickActions`; `null` = never
 * customized → `DEFAULT_QUICK_ACTION_KEYS`) and is re-filtered by the user's
 * CURRENT permissions and menu access on every render, so a saved key the
 * user has since lost access to never shows.
 */
export function QuickActionsMenu() {
  const { user, hasPermission, hasMenuAccess } = useAuth();
  const [open, setOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [addFollowUpOpen, setAddFollowUpOpen] = useState(false);

  const availableActions = useMemo(
    () =>
      QUICK_ACTION_CATALOG.filter(
        (a) =>
          (!a.requiredPermission || hasPermission(a.requiredPermission)) &&
          hasMenuAccess(a.requiredMenuKey),
      ),
    [hasPermission, hasMenuAccess],
  );
  const availableKeys = new Set(availableActions.map((a) => a.key));
  const savedKeys = user?.dashboardQuickActions ?? DEFAULT_QUICK_ACTION_KEYS;
  const selectedKeys = new Set(savedKeys.filter((k) => availableKeys.has(k)));
  const shownActions = availableActions.filter((a) => selectedKeys.has(a.key));

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Nothing this user could ever add - no menu at all.
  if (availableActions.length === 0) return null;

  const close = () => setOpen(false);

  return (
    <div className="ne-quick">
      <button
        type="button"
        className="ne-quick__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Quick actions"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ne-quick__plus" aria-hidden="true">
          +
        </span>
        <span className="ne-quick__trigger-label">Quick actions</span>
      </button>

      {open ? (
        <>
          <div className="ne-quick__backdrop" aria-hidden="true" onClick={close} />
          <div className="ne-quick__panel" role="menu" aria-label="Quick actions">
            <div className="ne-quick__head">Quick actions</div>
            {shownActions.length === 0 ? (
              <p className="ne-quick__empty">No shortcuts chosen yet.</p>
            ) : (
              <div className="ne-quick__list">
                {shownActions.map((a) => {
                  const body = (
                    <>
                      <span className="ne-quick__icon">
                        <NavIcon name={ICON[a.key] ?? 'zap'} width={16} height={16} />
                      </span>
                      <span className="ne-quick__text">
                        <span className="ne-quick__label">{a.label}</span>
                        <span className="ne-quick__group">{a.group}</span>
                      </span>
                    </>
                  );
                  return a.href ? (
                    <Link
                      key={a.key}
                      to={a.href}
                      role="menuitem"
                      className="ne-quick__item"
                      onClick={close}
                    >
                      {body}
                    </Link>
                  ) : (
                    <button
                      key={a.key}
                      type="button"
                      role="menuitem"
                      className="ne-quick__item"
                      onClick={() => {
                        close();
                        if (a.key === 'add_followup') setAddFollowUpOpen(true);
                      }}
                    >
                      {body}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="ne-quick__foot">
              <button
                type="button"
                className="ne-quick__customize"
                onClick={() => {
                  close();
                  setCustomizeOpen(true);
                }}
              >
                Customize…
              </button>
            </div>
          </div>
        </>
      ) : null}

      {customizeOpen ? (
        <CustomizeQuickActionsModal
          availableActions={availableActions}
          selectedKeys={selectedKeys}
          onClose={() => setCustomizeOpen(false)}
        />
      ) : null}
      {addFollowUpOpen ? (
        <AddFollowUpQuickModal initialDay={new Date()} onClose={() => setAddFollowUpOpen(false)} />
      ) : null}
    </div>
  );
}
